import { ChangeEvent } from 'react';
import { Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';

interface ModelGuidanceFormProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  isSaving: boolean;
  isDirty: boolean;
  maxLength: number;
}

const guidanceExamples = [
  'Add your name if you would like the model to use your name in responses.',
  'Add your general work overview. For example, you could mention that you are a copywriter or a marketer specializing in specific industries.',
];

const guidanceIntroText = 'This beta experiment lets you guide NoteGo models with a short introduction about yourself.';
const guidanceDetailsText =
  'The information you share will appear in your user profile as model guidance and can help tailor responses to your needs.';

const ModelGuidanceForm = ({ value, onChange, onSave, isSaving, isDirty, maxLength }: ModelGuidanceFormProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const charactersUsed = value.length;

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'neutral.otherBorder',
        boxShadow: 'none',
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Share a bit about your work
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {guidanceIntroText}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {guidanceDetailsText}
            </Typography>
          </Box>

          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {guidanceExamples.map((example) => (
              <Typography key={example} component="li" variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {example}
              </Typography>
            ))}
          </Box>

          <Stack spacing={1}>
            <TextField
              value={value}
              onChange={handleChange}
              multiline
              minRows={5}
              maxRows={10}
              placeholder={guidanceExamples.map((example) => `• ${example}`).join('\n')}
              inputProps={{ maxLength }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {charactersUsed}/{maxLength} characters
            </Typography>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={onSave} disabled={!isDirty || isSaving}>
              {isSaving ? 'Saving…' : 'Save guidance'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ModelGuidanceForm;
